"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useAdminUsers } from "@/lib/hooks/api/admin";
import { getSupabaseWithLogging } from "@/lib/hooks/api/utils";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { AppUser } from "@/lib/types/database";
import type { Contact_TasksDetail } from "@/lib/types/task-extensions";
import { TaskAssigneeCard } from "./TaskAssigneeCard";
import { collectAssigneeIdsFromTask } from "./assignee-utils";

export function TaskAssigneesDisplay({ item }: TabContentProps<Contact_TasksDetail>) {
  const { data: allUsers = [] } = useAdminUsers();

  const assigneeRows = React.useMemo(() => {
    const ids = collectAssigneeIdsFromTask(item);
    return ids.map((userId) => {
      const user = (allUsers as AppUser[]).find((u) => u.id === userId) || null;
      return { userId, user };
    });
  }, [item, allUsers]);

  const missingUserIds = React.useMemo(
    () => assigneeRows.filter((r) => !r.user).map((r) => r.userId),
    [assigneeRows]
  );

  const { data: supplementalUsers = {} } = useQuery({
    queryKey: ["app-users-task-assignees", [...missingUserIds].sort().join("|")],
    enabled: missingUserIds.length > 0,
    queryFn: async () => {
      const supabase = getSupabaseWithLogging();
      const { data, error } = await supabase
        .from("app_users")
        .select("id, first_name, last_name, email, is_active, role, role_id, created_at")
        .in("id", missingUserIds);
      if (error) throw new Error(error.message);
      const map: Record<string, AppUser> = {};
      for (const row of data ?? []) {
        map[row.id] = row as AppUser;
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  const mergedRows = React.useMemo(
    () =>
      assigneeRows.map(({ userId, user }) => ({
        userId,
        user: user ?? supplementalUsers[userId] ?? null,
      })),
    [assigneeRows, supplementalUsers]
  );

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        <h3 className="text-lg font-semibold text-card-foreground">Assigned to</h3>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        People assigned to <span className="font-medium text-card-foreground">{item.title}</span>.
      </p>

      {mergedRows.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-gray-600 dark:text-gray-400">
            No one is assigned yet. Assign users from the task details sidebar or edit flow.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {mergedRows.map(({ userId, user }) => (
            <TaskAssigneeCard key={userId} userId={userId} user={user} />
          ))}
        </div>
      )}
    </div>
  );
}
